import { useState } from 'react';
import { shuffleArray } from 'shared-helpers/common';
import { useDispatch, useReduxState } from 'hooks';
import { useAnswerSubmissionTracker, useApiHandlers } from 'lib/reader';
import {
  Alert,
  Button,
  ContentBox,
  RichTextValue,
  SortableList
} from 'components/common';
import { TaskPointsBadge } from 'components/common/tasks';
import { ReaderAnswerDelete, ReaderTaskHeader } from 'components/reader/common';
import { ReaderBlockSequencingOption } from '.';

import type { ButtonSize } from 'components/common/Button';
import type { CommonBlockPropsReader } from 'components/reader/blocks';

const ReaderBlockSequencing = ({
  answerData,
  blockId,
  data,
  font,
  fontSize,
  onAnswerSubmitted
}: CommonBlockPropsReader<'sequencing'>) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: {
      chapterData,
      isCompleted,
      offline,
      presentMode,
      readerCourseData: { course },
      readOnly
    },
    theme
  } = useReduxState();

  const { onAnswerSubmitted: submit } = useApiHandlers();

  const answerDataSent = Boolean(answerData?.id);

  const [answers, setAnswers] = useState<string[]>(
    answerData?.answers
      ? answerData.answers
      : data.useRandomOrder && !readOnly
      ? shuffleArray([...data.optionIds])
      : data.optionIds
  );
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(!presentMode);

  useAnswerSubmissionTracker({
    blockId,
    dirty,
    disabled: answerDataSent
  });

  const getAnswerStatuses = () => {
    const answerStatuses = answers.map(
      (answer, index) => data.options?.[index]?.id === answer
    );
    return answerStatuses;
  };

  const handleMoveAnswer = (index: number, increment: -1 | 1) => {
    const targetIndex = index + increment;
    if (targetIndex < 0 || targetIndex >= answers.length) return;

    const newAnswers = [...answers];
    const [moved] = newAnswers.splice(index, 1);
    newAnswers.splice(targetIndex, 0, moved);

    setAnswers(newAnswers);
    setDirty(true);
  };

  const handleSubmit = async () => {
    if (!submit) return;
    setLoading(true);

    const answerData = await submit({
      answers: { answers },
      blockId,
      type: 'sequencing'
    });
    if (!answerData) setError(true);
    else {
      dispatch.updatePageState({
        chapterData: {
          ...chapterData,
          blocks: {
            ...chapterData.blocks,
            [blockId]: { ...chapterData.blocks[blockId], answerData }
          }
        }
      });

      setError(false);
      setDirty(false);
      if (onAnswerSubmitted) onAnswerSubmitted(blockId);
    }

    setLoading(false);
  };

  const toggleAnswer = () => setShow(!show);

  const answerStatuses = getAnswerStatuses();

  const numberOfCorrectAnswers = answerStatuses.filter(
    (status) => status
  ).length;

  const feedback: {
    id: 'feedbackCorrect' | 'feedbackIncorrect' | 'feedbackPartiallyCorrect';
    message: string;
    type: 'success-light' | 'error-light' | 'warning-light';
  } = {
    id: 'feedbackPartiallyCorrect',
    message: messages.readerQuestionPartiallyCorrectAnswer,
    type: 'warning-light'
  };
  if (numberOfCorrectAnswers === answers?.length) {
    feedback.id = 'feedbackCorrect';
    feedback.message = messages.commonCorrectAnswer;
    feedback.type = 'success-light';
  } else if (numberOfCorrectAnswers === 0) {
    feedback.id = 'feedbackIncorrect';
    feedback.message = messages.commonIncorrectAnswer;
    feedback.type = 'error-light';
  }

  const isDisabled =
    answerDataSent ||
    readOnly ||
    (isCompleted && !course?.completionAllowAnswersAfterFinishing);

  return (
    <div className="sequencing">
      <ContentBox size={fontSize}>
        <ReaderTaskHeader
          blockType="sequencing"
          fontSize={fontSize}
          messages={messages}
          isMandatory={data?.isMandatory}
          theme={theme}
        />

        <div className="content">
          <div
            className="question"
            style={{
              fontFamily: font,
              fontSize: {
                small: '1.5rem',
                medium: '1.6rem',
                large: '1.8rem',
                huge: '2.2rem'
              }[fontSize]
            }}
          >
            <RichTextValue html={data.question} />
          </div>
          <div className="sequencing-info">
            <Alert text={messages.blocksSequencingInfo} type="info-dark" />
          </div>

          <SortableList
            disabled={isDisabled}
            items={answers}
            itemStyles={{ backgroundColor: 'transparent', padding: 0 }}
            listId={`reader-block-sequencing-${blockId}`}
            onChange={(newAnswers) => {
              setAnswers(newAnswers);
              setDirty(true);
            }}
            renderItem={(answerId, index) => {
              const option = data.options.find(
                (option) => option.id === answerId
              );
              if (!option) return null;

              return (
                <ReaderBlockSequencingOption
                  disabled={isDisabled}
                  isCorrect={answerStatuses[index]}
                  isFirst={index === 0}
                  isLast={index === answers.length - 1}
                  key={option.id}
                  onMoveDown={() => handleMoveAnswer(index, 1)}
                  onMoveUp={() => handleMoveAnswer(index, -1)}
                  showIcon={answerDataSent}
                  text={option.text}
                />
              );
            }}
            syncWithParent={true}
          />

          <div className="submit">
            {answerDataSent && presentMode ? (
              <Button
                onClick={toggleAnswer}
                size={
                  {
                    small: 'medium',
                    medium: 'medium',
                    large: 'large',
                    huge: 'large'
                  }[fontSize] as ButtonSize
                }
                text={
                  presentMode && show
                    ? messages.readerQuestionAnswerHide
                    : messages.readerQuestionAnswerShow
                }
                type="button"
              />
            ) : (
              <Button
                category="success"
                disabled={
                  answerDataSent ||
                  offline ||
                  !submit ||
                  readOnly ||
                  (isCompleted && !course?.completionAllowAnswersAfterFinishing)
                }
                loading={loading}
                size={fontSize}
                text={messages.commonSubmit}
                tooltip={
                  offline
                    ? messages.readerAnswerOfflineWarning
                    : isCompleted &&
                      !course?.completionAllowAnswersAfterFinishing
                    ? messages.readerAnswerIsCompletedWarning
                    : undefined
                }
                type="submit"
                onClick={handleSubmit}
                style={{ marginTop: '10px' }}
              />
            )}
          </div>

          {error ? (
            <div className="error">
              <Alert
                size={fontSize}
                text={messages.commonError}
                type="error-light"
              />
            </div>
          ) : answerDataSent && (!presentMode || show) ? (
            <div className="feedback">
              <Alert
                size={fontSize}
                style={{ marginTop: 10 }}
                text={
                  <RichTextValue
                    html={
                      data[feedback.id] && data[feedback.id] !== '<p></p>'
                        ? data[feedback.id]
                        : `<p>${feedback.message}</p>`
                    }
                  />
                }
                type={feedback.type}
              />
            </div>
          ) : null}
        </div>

        <div className="answered">
          {answerData?.maxPoints ? (
            <TaskPointsBadge
              hideSuccessBadge={true}
              maxPoints={answerData.maxPoints}
              points={answerData.points}
            />
          ) : null}

          <ReaderAnswerDelete answerId={answerData?.id} blockId={blockId} />
        </div>
      </ContentBox>

      <style jsx>{`
        .sequencing {
          position: relative;
        }
        .feedback-content :global(p) {
          margin: 8px 0 0;
        }
        .feedback-content :global(p:first-child) {
          margin-top: 0;
        }
        .sequencing-info {
          margin: 10px 0;
        }
        .submit {
          margin-top: 5px;
        }
        .answered {
          align-items: center;
          display: flex;
          justify-content: space-between;
        }
      `}</style>
    </div>
  );
};

export default ReaderBlockSequencing;
